import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useStats } from "@/lib/useStats";
import { useUser } from "@/store/user";
import { CertificateLink } from "@/components/CertificateLink";

function StatTile({ icon, value, label, color }) {
  return (
    <div className="dash-stat" data-testid={`dash-stat-${label}`}>
      <div className="dash-stat-icon">{icon}</div>
      <div className="dash-stat-value" style={{ color: color || "var(--gold)" }}>
        {value}
      </div>
      <div className="dash-stat-label">{label}</div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, userId, progress, progressVersion, isAuthed } = useUser();
  const stats = useStats();
  const [tracks, setTracks] = useState([]);
  const lessonsTotal = stats.lessons;

  // Refetch when the user identity changes OR progress bumps (not on length).
  useEffect(() => {
    api.listTracks().then((d) => setTracks(d.tracks || [])).catch(() => {});
  }, [userId, progressVersion]);

  const completed = useMemo(
    () => new Set(progress.completed_lessons || []),
    [progress.completed_lessons]
  );
  const completedCount = completed.size;
  const progressPct = lessonsTotal
    ? Math.round((completedCount / lessonsTotal) * 100)
    : 0;

  // Certificates earned = tracks where the user passed the final test
  const earnedCerts = useMemo(() => {
    return tracks.filter((t) => t.certificate_eligible);
  }, [tracks]);

  const totalCerts = useMemo(
    () => tracks.filter((t) => t.final_test_id).length || 7,
    [tracks]
  );

  const xp = progress.xp || 0;
  const streak = progress.streak || 0;
  const badges = progress.badges?.length || 0;

  const displayName =
    user?.name ||
    (user?.email ? user.email.split("@")[0] : null) ||
    "متعلّم بَلاغة";

  return (
    <div className="shell has-bottom-nav" data-testid="dashboard-page">
      {/* ===== HEADER ===== */}
      <div className="page-header dash-header">
        <div className="dash-avatar">
          {user?.picture ? (
            <img src={user.picture} alt="" />
          ) : (
            <span>{displayName.charAt(0)}</span>
          )}
        </div>
        <h1>
          مرحبًا، <span className="gold">{displayName}</span>
        </h1>
        <p className="hero-sub">
          هذه لوحتُك الشخصيّة — تَتَبَّع تَقدّمك، شهاداتك، ونقاطك.
        </p>
        {!isAuthed && (
          <button
            className="btn btn-primary"
            onClick={() => navigate("/login")}
            data-testid="dash-signin-btn"
          >
            🔐 سَجِّل دخول لحفظ تَقدّمك بشكلٍ دائم
          </button>
        )}
      </div>

      {/* ===== TOP STATS ===== */}
      <section className="dash-stats-grid" data-testid="dash-stats-grid">
        <StatTile
          icon="📚"
          value={`${completedCount}/${lessonsTotal}`}
          label="دروس مُكتَمَلة"
        />
        <StatTile
          icon="📈"
          value={`${progressPct}%`}
          label="نسبة التَقدّم الإجماليّة"
          color="#2ec48e"
        />
        <StatTile
          icon="🏅"
          value={`${earnedCerts.length}/${totalCerts}`}
          label="شهادات مكتسبة"
          color="#f5c87a"
        />
        <StatTile
          icon="⚡"
          value={xp}
          label="نقاط الخبرة (XP)"
          color="#8b6df0"
        />
        <StatTile
          icon="🔥"
          value={streak}
          label="سلسلة الأيّام"
          color="#ff9d6a"
        />
        <StatTile
          icon="🎖️"
          value={badges}
          label="شارات الإنجاز"
          color="#c9a8e6"
        />
      </section>

      {/* ===== PER-TRACK PROGRESS ===== */}
      <section className="dash-card" data-testid="dash-tracks-progress">
        <h2>📊 تَقدّمك في كلّ مسار</h2>
        {tracks.length === 0 && (
          <div className="dash-empty">جارٍ التحميل…</div>
        )}
        <div className="dash-tracks">
          {tracks
            .filter((t) => t.status === "available")
            .map((t) => (
              <div
                key={t.id}
                className="dash-track-row"
                data-testid={`dash-track-${t.id}`}
                onClick={() => navigate("/tracks")}
              >
                <div className="dash-track-head">
                  <span style={{ color: t.accent || "var(--gold)" }}>
                    {t.icon} {t.title}
                  </span>
                  <span className="dash-track-sub">{t.subtitle}</span>
                </div>
                <div className="dash-track-bar">
                  <div
                    className="dash-track-fill"
                    style={{
                      width: `${t.stats?.progress_pct || 0}%`,
                      background: t.accent || "var(--gold)",
                    }}
                  />
                </div>
                <div className="dash-track-meta">
                  <span>
                    {t.stats?.completed_lessons || 0}/{t.stats?.total_lessons || 0}
                  </span>
                  <span className="dash-track-pct">
                    {t.stats?.progress_pct || 0}%
                  </span>
                  {t.certificate_eligible && (
                    <span className="dash-track-cert">🏅 شهادة جاهزة</span>
                  )}
                </div>
              </div>
            ))}
        </div>
      </section>

      {/* ===== EARNED CERTIFICATES ===== */}
      {earnedCerts.length > 0 && (
        <section className="dash-card" data-testid="dash-certificates">
          <h2>🏅 شهاداتك المكتسبة</h2>
          <div className="dash-certs">
            {earnedCerts.map((t) => (
              <div key={t.id} className="dash-cert-card">
                <div className="dash-cert-icon" style={{ color: t.accent }}>
                  {t.icon}
                </div>
                <div>
                  <strong>{t.title}</strong>
                  <em>{t.subtitle}</em>
                </div>
                <CertificateLink
                  trackId={t.id}
                  className="btn btn-gold btn-sm"
                  testId={`dash-cert-download-${t.id}`}
                >
                  تحميل
                </CertificateLink>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ===== QUICK NAV ===== */}
      <section className="dash-card" data-testid="dash-quick-nav">
        <h2>🚀 الخطوة التالية</h2>
        <div className="dash-actions">
          <button
            className="btn btn-primary"
            onClick={() => navigate("/tracks")}
            data-testid="dash-go-tracks"
          >
            🗺️ متابعة المسارات
          </button>
          <button
            className="btn btn-outline"
            onClick={() => navigate("/methodology")}
            data-testid="dash-go-methodology"
          >
            📚 المنهج الكامل
          </button>
          <button
            className="btn btn-outline"
            onClick={() => navigate("/chat")}
            data-testid="dash-go-chat"
          >
            💬 المعلّم الذكيّ
          </button>
        </div>
      </section>
    </div>
  );
}
